// export.ts — CSV export of the plant-care log (all local; nothing leaves the device).
// One row per care event, joined to its plant. Built entirely client-side.

import type { CareEvent, Plant } from "./store";
import { kindLabel } from "./store";

function cell(v: string | number): string {
  const s = String(v ?? "");
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
function csv(headers: string[], rows: (string | number)[][]): string {
  return [headers, ...rows].map((r) => r.map(cell).join(",")).join("\n");
}

/** Full CSV: Plant, Species, Location, Date, Action, Note. */
export function careLogCsv(plants: Plant[], events: CareEvent[]): string {
  const byId = new Map(plants.map((p) => [p.id, p]));
  // Sort by plant name, then date ascending — a readable chronological log per plant.
  const sorted = [...events].sort((a, b) => {
    const pa = byId.get(a.plant_id)?.name ?? "";
    const pb = byId.get(b.plant_id)?.name ?? "";
    return pa.localeCompare(pb) || a.done_on.localeCompare(b.done_on);
  });
  return csv(
    ["Plant", "Species", "Location", "Date", "Action", "Note"],
    sorted.map((e) => {
      const p = byId.get(e.plant_id);
      return [p?.name ?? "", p?.species ?? "", p?.location ?? "", e.done_on, kindLabel(e.kind), e.note ?? ""];
    }),
  );
}

export function buildExport(plants: Plant[], events: CareEvent[]): { csv: string; filename: string } {
  const stamp = new Date().toISOString().slice(0, 10);
  return { csv: careLogCsv(plants, events), filename: `plant-care-log-${stamp}.csv` };
}
